import { Connection, NodeEditor } from "rete"

type MenuState = {
  element: HTMLDivElement
  connection: Connection
  cleanup: () => void
}

type BoundConnectionElement = HTMLElement & {
  __edgeContextMenuBound?: boolean
}

type EditorWithOff = NodeEditor & {
  off: (event: string, handler: (...args: unknown[]) => void) => void
}

const hasOff = (editor: NodeEditor): editor is EditorWithOff =>
  typeof (editor as Partial<EditorWithOff>).off === "function"

export function enableConnectionDeleteContextMenu(editor: NodeEditor) {
  if (typeof document === "undefined") {
    return () => {}
  }

  let currentMenu: MenuState | null = null

  const removeMenu = () => {
    if (!currentMenu) return
    currentMenu.cleanup()
    currentMenu.element.remove()
    currentMenu = null
  }

  const createMenuElement = (event: MouseEvent, connection: Connection) => {
    event.preventDefault()
    event.stopPropagation()
    removeMenu()

    const menu = document.createElement("div")
    menu.className = "context-menu edge-context-menu"
    const scopedAttr = "data-v-4da435cc"
    menu.setAttribute(scopedAttr, "")
    menu.style.position = "fixed"
    menu.style.top = "0px"
    menu.style.left = "0px"
    menu.style.zIndex = "10000"

    const deleteItem = document.createElement("div")
    deleteItem.textContent = "Delete edge"
    deleteItem.className = "item edge-context-menu__button"
    deleteItem.setAttribute(scopedAttr, "")
    deleteItem.tabIndex = 0

    const deleteHandler = () => {
      editor.removeConnection(connection)
      removeMenu()
    }
    const deleteKeyHandler = (evt: KeyboardEvent) => {
      if (evt.key === "Enter" || evt.key === " ") {
        evt.preventDefault()
        deleteHandler()
      }
    }
    deleteItem.addEventListener("click", deleteHandler)
    deleteItem.addEventListener("keydown", deleteKeyHandler)
    menu.appendChild(deleteItem)
    document.body.appendChild(menu)

    const padding = 8
    const preferredLeft = Math.min(
      Math.max(event.clientX, padding),
      window.innerWidth - menu.offsetWidth - padding
    )
    const preferredTop = Math.min(
      Math.max(event.clientY, padding),
      window.innerHeight - menu.offsetHeight - padding
    )
    menu.style.left = `${preferredLeft}px`
    menu.style.top = `${preferredTop}px`

    const handleOutsideInteraction = (evt: MouseEvent | PointerEvent) => {
      if (!menu.contains(evt.target as Node)) {
        removeMenu()
      }
    }

    const handleKeyboard = (evt: KeyboardEvent) => {
      if (evt.key === "Escape") {
        removeMenu()
      }
    }

    document.addEventListener("mousedown", handleOutsideInteraction, true)
    document.addEventListener("contextmenu", handleOutsideInteraction, true)
    document.addEventListener("wheel", handleOutsideInteraction, true)
    document.addEventListener("keydown", handleKeyboard, true)

    currentMenu = {
      connection,
      element: menu,
      cleanup: () => {
        deleteItem.removeEventListener("click", deleteHandler)
        deleteItem.removeEventListener("keydown", deleteKeyHandler)
        document.removeEventListener("mousedown", handleOutsideInteraction, true)
        document.removeEventListener("contextmenu", handleOutsideInteraction, true)
        document.removeEventListener("wheel", handleOutsideInteraction, true)
        document.removeEventListener("keydown", handleKeyboard, true)
      },
    }
  }

  const onRenderConnection = ({ el, connection }: { el: HTMLElement; connection: Connection }) => {
    const boundEl = el as BoundConnectionElement
    if (boundEl.__edgeContextMenuBound) return
    const handler = (event: MouseEvent) => createMenuElement(event, connection)
    el.addEventListener("contextmenu", handler)
    boundEl.__edgeContextMenuBound = true
  }

  const onConnectionRemoved = (connection: Connection) => {
    if (currentMenu?.connection === connection) {
      removeMenu()
    }
  }

  const onEditorDestroy = () => {
    removeMenu()
  }

  editor.on("renderconnection", onRenderConnection)
  editor.on("connectionremoved", onConnectionRemoved)
  editor.on("destroy", onEditorDestroy)

  return () => {
    if (hasOff(editor)) {
      editor.off("renderconnection", onRenderConnection)
      editor.off("connectionremoved", onConnectionRemoved)
      editor.off("destroy", onEditorDestroy)
    }
    removeMenu()
  }
}


